import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PremiumLock from '../components/PremiumLock';
import MiniMetronome from '../components/MiniMetronome';
import TabNotation from '../components/TabNotation';
import { toast } from 'sonner';
import { Dumbbell, Filter, Clock, Crown, Play, CheckCircle, Check, X, Target, Lightbulb, Gauge, BookOpen, ChevronRight, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExercisesPage = () => {
  const { t, i18n } = useTranslation();
  const { isPremium, isAuthenticated } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [openChapters, setOpenChapters] = useState({});
  const toggleChapter = (key) => setOpenChapters((o) => ({ ...o, [key]: !o[key] }));
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [completedIds, setCompletedIds] = useState(new Set());
  const [savingId, setSavingId] = useState(null);
  const [lessonsById, setLessonsById] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  useEffect(() => {
    fetchExercises();
    fetchLessonsCatalog();
    if (isAuthenticated) {
      fetchProgress();
    }
  }, [isAuthenticated]);

  // Load the lightweight lesson list once so the exercise detail can
  // resolve related_lesson_id to a title without N+1 detail fetches.
  const fetchLessonsCatalog = async () => {
    try {
      const res = await axios.get(`${API}/lessons`);
      setLessonsById(Object.fromEntries((res.data || []).map((l) => [l.id, l])));
    } catch (error) {
      console.error('Error fetching lessons catalog:', error);
    }
  };

  useEffect(() => {
    if (!focusId || exercises.length === 0 || selectedExercise) return;
    const target = exercises.find(e => e.id === focusId);
    if (target) {
      setSelectedExercise(target);
      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, exercises]);

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API}/exercises`);
      setExercises(response.data);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress?item_type=exercise`);
      const completed = new Set(
        response.data.filter(p => p.status === 'completed').map(p => p.item_id)
      );
      setCompletedIds(completed);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const markCompleted = async (exerciseId) => {
    if (!isAuthenticated) {
      toast.error(t('exercises.saveLogin'));
      return;
    }

    setSavingId(exerciseId);
    try {
      await axios.post(`${API}/progress?item_type=exercise&item_id=${exerciseId}&status=completed`);
      setCompletedIds(prev => new Set([...prev, exerciseId]));
      toast.success(t('exercises.completed'));
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error(t('exercises.saveError'));
    } finally {
      setSavingId(null);
    }
  };

  const filteredExercises = exercises.filter(ex => {
    if (selectedDifficulty !== 'all' && ex.difficulty !== selectedDifficulty) return false;
    if (selectedCategory !== 'all' && ex.category !== selectedCategory) return false;
    return true;
  });

  const categories = [...new Set(exercises.map(e => e.category))];
  const difficulties = ['beginner', 'intermediate', 'advanced', 'master'];

  // Chapter grouping by difficulty (only when no specific difficulty
  // filter is active). Matches the TheoryPage materia chapter pattern.
  const DIFF_ORDER = ['beginner', 'intermediate', 'advanced', 'master'];
  const groupedChapters = selectedDifficulty === 'all'
    ? DIFF_ORDER
        .map(d => ({ difficulty: d, items: filteredExercises.filter(e => e.difficulty === d) }))
        .filter(c => c.items.length > 0)
    : null;

  const getTitle = (ex) => i18n.language === 'it' ? ex.title_it : ex.title;
  const getDesc = (ex) => i18n.language === 'it' ? ex.description_it : ex.description;
  const getInstructions = (ex) =>
    (i18n.language === 'it' ? ex.instructions_it : ex.instructions) || [];
  const getTips = (ex) =>
    (i18n.language === 'it' ? ex.tips_it : ex.tips) || [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-6 md:px-12" data-testid="exercises-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue to-cyan-400 flex items-center justify-center">
              <Dumbbell className="w-8 h-8 text-black" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white">
              {t('exercises.title')}
            </h1>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap gap-4 mb-8"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-400" />
            <span className="text-zinc-400 text-sm">{t('common.filter')}:</span>
          </div>
          
          <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
            <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white" data-testid="difficulty-filter">
              <SelectValue placeholder={t('exercises.difficulty')} />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all">{t('common.all')}</SelectItem>
              {difficulties.map(d => (
                <SelectItem key={d} value={d}>{t(`levels.${d}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white" data-testid="category-filter">
              <SelectValue placeholder={t('exercises.category')} />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all">{t('common.all')}</SelectItem>
              {categories.map(c => (
                <SelectItem key={c} value={c}>{t(`exercises.categories.${c}`) || c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-blue border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Exercises Grid */}
        {!loading && (() => {
          const renderCard = (exercise, index) => (
            <motion.div
              key={exercise.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="relative bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-blue/30 transition-all group"
              data-testid={`exercise-card-${exercise.id}`}
            >
              {exercise.is_premium && !isPremium && <PremiumLock />}

              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${
                  exercise.difficulty === 'beginner' ? 'from-green-500 to-emerald-400' :
                  exercise.difficulty === 'intermediate' ? 'from-blue to-cyan-400' :
                  'from-purple-500 to-pink-500'
                } flex items-center justify-center`}>
                  <Dumbbell className="w-6 h-6 text-black" />
                </div>

                {exercise.is_premium && (
                  <Crown className="w-5 h-5 text-amber" />
                )}
              </div>

              <h3 className="font-heading text-lg font-bold text-white mb-2">
                {getTitle(exercise)}
              </h3>
              <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
                {getDesc(exercise)}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <span className={`px-2 py-1 rounded-full ${
                    exercise.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' :
                    exercise.difficulty === 'intermediate' ? 'bg-blue/20 text-blue' :
                    'bg-purple-500/20 text-purple-400'
                  }`}>
                    {t(`levels.${exercise.difficulty}`)}
                  </span>
                  <span className="flex items-center gap-1 text-zinc-500">
                    <Clock className="w-4 h-4" />
                    {exercise.duration_minutes} min
                  </span>
                </div>
              </div>

              {(!exercise.is_premium || isPremium) && (
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => setSelectedExercise(exercise)}
                    className="flex-1 bg-gradient-to-r from-blue to-cyan-400 text-black font-semibold"
                    data-testid={`start-exercise-${exercise.id}`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {t('exercises.start')}
                  </Button>

                  {completedIds.has(exercise.id) ? (
                    <Button
                      disabled
                      className="bg-green-500/20 text-green-400 cursor-default"
                      data-testid={`completed-${exercise.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => markCompleted(exercise.id)}
                      disabled={savingId === exercise.id}
                      variant="outline"
                      className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                      data-testid={`mark-complete-${exercise.id}`}
                    >
                      {savingId === exercise.id ? (
                        <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              )}
            </motion.div>
          );

          if (groupedChapters) {
            return (
              <div className="space-y-4">
                {groupedChapters.map((chapter, chapterIdx) => {
                  const open = !!openChapters[chapter.difficulty];
                  return (
                    <section key={chapter.difficulty} data-testid={`chapter-${chapter.difficulty}`}>
                      <motion.button
                        type="button"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: chapterIdx * 0.04 }}
                        onClick={() => toggleChapter(chapter.difficulty)}
                        aria-expanded={open}
                        className={`w-full flex items-baseline justify-between gap-3 border-b border-white/5 pb-3 text-left hover:border-blue/30 transition-colors ${open ? 'mb-4' : ''}`}
                      >
                        <div className="flex items-baseline gap-3">
                          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                            {t(`levels.${chapter.difficulty}`)}
                          </h2>
                          <span className="text-zinc-500 text-sm font-mono">
                            {chapter.items.length}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-zinc-500 transition-transform duration-200 self-center ${open ? '' : '-rotate-90'}`}
                        />
                      </motion.button>
                      {open && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {chapter.items.map((exercise, i) => renderCard(exercise, i))}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            );
          }
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredExercises.map((exercise, index) => renderCard(exercise, index))}
            </div>
          );
        })()}

        {/* Exercise Detail Modal */}
        {selectedExercise && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-md"
            onClick={() => setSelectedExercise(null)}
          >
            <motion.article
              initial={{ scale: 0.95, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
              className="relative bg-[#0E0E10] border border-white/10 rounded-3xl shadow-2xl shadow-cyan-900/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Close button - sticky top-right */}
              <button
                onClick={() => setSelectedExercise(null)}
                className="sticky top-4 float-right z-10 mr-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 backdrop-blur flex items-center justify-center text-zinc-300 hover:text-white transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Hero header */}
              <header className="relative px-8 sm:px-12 pt-12 pb-10 border-b border-white/5 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/15 via-blue/5 to-transparent pointer-events-none" />
                <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />

                <div className="relative">
                  <div className="flex flex-wrap items-center gap-3 mb-5 text-xs uppercase tracking-[0.18em] font-semibold">
                    {selectedExercise.category && (
                      <span className="px-2.5 py-1 rounded-full bg-blue/15 border border-blue/20 text-blue">
                        {t(`exercises.categories.${selectedExercise.category}`, { defaultValue: selectedExercise.category })}
                      </span>
                    )}
                    <span className="text-zinc-600">·</span>
                    <span className={`px-2.5 py-1 rounded-full border ${
                      selectedExercise.difficulty === 'beginner' ? 'bg-green-500/10 border-green-500/20 text-green-400' :
                      selectedExercise.difficulty === 'intermediate' ? 'bg-blue/10 border-blue/20 text-blue' :
                      'bg-amber/10 border-amber/20 text-amber'
                    }`}>
                      {t(`levels.${selectedExercise.difficulty}`)}
                    </span>
                    {selectedExercise.duration_minutes && (
                      <>
                        <span className="text-zinc-600">·</span>
                        <span className="flex items-center gap-1 text-zinc-400 normal-case tracking-normal">
                          <Clock className="w-3.5 h-3.5" />
                          {selectedExercise.duration_minutes} min
                        </span>
                      </>
                    )}
                  </div>

                  <div className="flex items-start gap-6">
                    <div className="hidden sm:flex shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-blue/30 to-cyan-500/20 border border-blue/30 items-center justify-center">
                      <Dumbbell className="w-8 h-8 text-cyan-300" />
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-white leading-tight tracking-tight">
                      {getTitle(selectedExercise)}
                    </h1>
                  </div>

                  {getDesc(selectedExercise) && (
                    <p className="relative mt-6 text-lg text-zinc-300 leading-relaxed max-w-3xl">
                      {getDesc(selectedExercise)}
                    </p>
                  )}

                  {selectedExercise.related_lesson_id && lessonsById[selectedExercise.related_lesson_id] && (
                    <div className="mt-6">
                      <Link
                        to={`/theory?focus=${selectedExercise.related_lesson_id}`}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-100 hover:bg-purple-500/20 text-sm transition-colors"
                      >
                        <BookOpen className="w-3.5 h-3.5" />
                        <span className="opacity-70">{t('exercises.relatedLesson')}:</span>
                        <span className="font-medium">
                          {i18n.language === 'it'
                            ? lessonsById[selectedExercise.related_lesson_id].title_it
                            : lessonsById[selectedExercise.related_lesson_id].title}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 opacity-70" />
                      </Link>
                    </div>
                  )}
                </div>
              </header>

              {/* Body */}
              <div className="px-8 sm:px-12 py-10 space-y-8">
                {/* BPM range + inline metronome */}
                {(selectedExercise.bpm_start || selectedExercise.bpm_target) && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    {selectedExercise.bpm_start && (
                      <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-5 py-4">
                        <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider mb-1">
                          <Gauge className="w-3.5 h-3.5" />
                          {t('exercises.bpmStart')}
                        </div>
                        <div className="font-mono text-2xl font-black text-amber">{selectedExercise.bpm_start}</div>
                      </div>
                    )}
                    {selectedExercise.bpm_target && (
                      <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-5 py-4">
                        <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider mb-1">
                          <Target className="w-3.5 h-3.5" />
                          {t('exercises.bpmTarget')}
                        </div>
                        <div className="font-mono text-2xl font-black text-green-400">{selectedExercise.bpm_target}</div>
                      </div>
                    )}
                    <div className="lg:col-span-1">
                      <MiniMetronome
                        initialBpm={selectedExercise.bpm_start || 100}
                        bpmMin={Math.max(40, (selectedExercise.bpm_start || 60) - 40)}
                        bpmMax={Math.min(240, (selectedExercise.bpm_target || 180) + 20)}
                        accent="blue"
                      />
                    </div>
                  </div>
                )}

                {!selectedExercise.bpm_start && !selectedExercise.bpm_target && (
                  <MiniMetronome initialBpm={100} accent="blue" />
                )}

                {/* Step-by-step instructions */}
                {getInstructions(selectedExercise).length > 0 && (
                  <div className="rounded-2xl bg-gradient-to-br from-blue/10 to-transparent border border-blue/20 p-6">
                    <div className="flex items-center gap-2 mb-5 text-blue">
                      <Target className="w-5 h-5" />
                      <h3 className="font-heading font-bold uppercase tracking-wider text-sm">
                        {t('exercises.instructions')}
                      </h3>
                    </div>
                    <ol className="space-y-4">
                      {getInstructions(selectedExercise).map((step, i) => (
                        <li key={i} className="flex items-start gap-4">
                          <span className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue/30 to-cyan-500/20 border border-blue/30 text-cyan-300 font-mono font-bold text-sm flex items-center justify-center">
                            {i + 1}
                          </span>
                          <span className="text-zinc-200 leading-relaxed pt-1">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Tab notation */}
                {selectedExercise.tab_notation && (
                  <TabNotation title={t('exercises.tabNotation').replace(':', '')}>
                    {selectedExercise.tab_notation}
                  </TabNotation>
                )}

                {/* Tips */}
                {getTips(selectedExercise).length > 0 && (
                  <div className="rounded-2xl bg-amber/5 border border-amber/20 p-6">
                    <div className="flex items-center gap-2 mb-4 text-amber">
                      <Lightbulb className="w-5 h-5" />
                      <h3 className="font-heading font-bold uppercase tracking-wider text-sm">
                        {t('exercises.tips')}
                      </h3>
                    </div>
                    <ul className="space-y-3">
                      {getTips(selectedExercise).map((tip, i) => (
                        <li key={i} className="flex items-start gap-3 text-zinc-200">
                          <span className="shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-amber" />
                          <span className="leading-relaxed">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Footer actions */}
              <footer className="sticky bottom-0 px-8 sm:px-12 py-5 bg-[#0E0E10]/95 backdrop-blur border-t border-white/5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                {!completedIds.has(selectedExercise.id) ? (
                  <Button
                    onClick={() => markCompleted(selectedExercise.id)}
                    disabled={savingId === selectedExercise.id}
                    className="flex-1 bg-gradient-to-r from-blue to-cyan-400 text-black font-semibold hover:opacity-90"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {t('exercises.start')}
                  </Button>
                ) : (
                  <div className="flex-1 flex items-center justify-center gap-2 text-green-400 font-semibold py-2">
                    <CheckCircle className="w-5 h-5" />
                    {t('exercises.completed')}
                  </div>
                )}
                <Button
                  variant="ghost"
                  onClick={() => setSelectedExercise(null)}
                  className="text-zinc-400 hover:text-white"
                >
                  {t('common.back')}
                </Button>
              </footer>
            </motion.article>
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && filteredExercises.length === 0 && (
          <div className="text-center py-20">
            <Dumbbell className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">{t('exercises.noResults')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExercisesPage;
